import { http } from './http'
import { auth } from './auth'
import { z } from 'zod'
import {
  AgendaObjectServerSchema,
  AgendaColumnSchema,
  AgendaSchema,
  ItemSchema,
  ActionItemSchema,
  CategorySchema,
} from '../schemas'
import {
  ItemPayload,
  ActionItemPayload,
  Category,
  UpdatePositionsPayload,
  BulkAddAgendaObjectsPayload,
  RerankAgendaObjectsPayload,
  UpdateNumberingPayload,
  ModifyAgendaColumnsPayload,
  DeleteAgendaColumnsPayload,
  CreateAgendaColumnsPayload,
  CompleteAgendaImportPayload,
} from '../types'
import { parseWithZod } from '../utils/parse-with-zod'

const AgendaImportSchema = z.object({
  id: z.number(),
  state: z.string(),
  upload_id: z.number().nullish(),
  progress_rate: z.number().nullish(),
  error_type: z.number().nullish(),
})

const AgendaImportsResponseSchema = z.object({
  agenda_imports: z.array(AgendaImportSchema),
})

const AgendaImportResponseSchema = z.object({
  agenda_import: AgendaImportSchema,
})

type UpdateAccessPayload = {
  gas_is_public: boolean
  gas_group_ids: string[]
  action_item_ids: string[]
  category_ids: string[]
  item_ids: string[]
}

type DmsRefDocument = {
  library_id: string
  document_id: number
  version: number
}

type CreateItemsFromDmsRefPayload = {
  documents: DmsRefDocument[]
  pre_agenda_object_id: number | null
  post_agenda_object_id: number | null
  async: true
}

const CreateItemsFromDmsRefResponseSchema = z.object({
  job_id: z.string(),
})

const AgendaContentSchema = z.object({
  agenda_objects: z.array(AgendaObjectServerSchema),
  action_items: z.array(ActionItemSchema),
  items: z.array(ItemSchema),
  categories: z.array(CategorySchema),
})

export const AgendaObjectListSchema = z.object({
  agenda_objects: z.array(AgendaObjectServerSchema),
})

export const AgendaResponseSchema = z.object({
  agenda: AgendaSchema,
})

const REQUIRED_COLUMN_TYPES = [
  'title',
  'responsibility',
  'schedule',
  'signature',
  'version',
  'note',
  'access',
] as const

const OPTIONAL_COLUMN_TYPES = ['text', 'date', 'number', 'dropdown', 'status-dropdown'] as const

export const AgendaColumnListSchema = z
  .object({
    agenda_columns: z.array(AgendaColumnSchema),
  })
  .refine(
    ({ agenda_columns }) => {
      const typeCount: Record<string, number> = {}

      for (const col of agenda_columns) {
        typeCount[col.column_type] = (typeCount[col.column_type] || 0) + 1
      }

      for (const type of REQUIRED_COLUMN_TYPES) {
        if (typeCount[type] !== 1) return false
      }

      const allAllowedTypes: string[] = [...REQUIRED_COLUMN_TYPES, ...OPTIONAL_COLUMN_TYPES]
      const invalidTypes = Object.keys(typeCount).filter(type => !allAllowedTypes.includes(type))
      if (invalidTypes.length > 0) return false

      return true
    },
    {
      error: `agenda_columns must have exactly one of each required column type: ${REQUIRED_COLUMN_TYPES}.`,
    },
  )

export const agenda = {
  getAgenda: async (agendaId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/agendas/${agendaId}`, undefined, dealId)
    const parsedResult = parseWithZod(AgendaResponseSchema, result)
    return parsedResult.agenda
  },

  getAgendaObjects: async (agendaId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/agendas/${agendaId}/agenda_objects`,
      undefined,
      dealId,
    )

    return parseWithZod(AgendaObjectListSchema, result).agenda_objects
  },

  getAgendaColumns: async (agendaId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/agendas/${agendaId}/agenda_columns`,
      undefined,
      dealId,
    )
    return parseWithZod(AgendaColumnListSchema, result).agenda_columns
  },

  updateItem: async (id: number, payload: ItemPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(`${regionHost}/api/items/${id}`, { item: payload }, dealId)
    return parseWithZod(z.object({ item: ItemSchema }), result).item
  },

  updateActionItem: async (id: number, payload: ActionItemPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(
      `${regionHost}/api/action_items/${id}`,
      { action_item: payload },
      dealId,
    )
    return parseWithZod(z.object({ action_item: ActionItemSchema }), result).action_item
  },

  updateCategory: async (id: number, payload: Category, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.put(
      `${regionHost}/api/categories/${id}`,
      { category: payload },
      dealId,
    )
    return parseWithZod(z.object({ category: CategorySchema }), result).category
  },

  updateAccess: async (id: number, payload: UpdateAccessPayload) => {
    const regionHost = auth.getUserRegionHost()
    return await http.post(`${regionHost}/api/deals/${id}/update_access`, payload)
  },

  updatePositions: async (id: number, payload: UpdatePositionsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/update_positions`,
      payload,
      dealId,
    )

    return parseWithZod(z.object({ agenda_objects: z.array(AgendaObjectServerSchema) }), result)
      .agenda_objects
  },

  copyAgendaObjects: async (id: number, payload: UpdatePositionsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/copy_agenda_objects`,
      payload,
      dealId,
    )

    return parseWithZod(AgendaContentSchema, result)
  },
  rerankAgendaObjects: async (id: number, payload: RerankAgendaObjectsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/rerank_agenda_objects`,
      payload,
      dealId,
    )

    return parseWithZod(z.object({ agenda_objects: z.array(AgendaObjectServerSchema) }), result)
      .agenda_objects
  },

  modifyAgendaColumns: async (id: number, payload: ModifyAgendaColumnsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/modify_agenda_columns`,
      payload,
      dealId,
    )
    return parseWithZod(AgendaColumnListSchema, result).agenda_columns
  },

  deleteAgendaColumns: async (id: number, payload: DeleteAgendaColumnsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/agendas/${id}/delete_agenda_columns`, payload, dealId)
  },

  createAgendaColumns: async (id: number, payload: CreateAgendaColumnsPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/create_agenda_columns`,
      payload,
      dealId,
    )
    return parseWithZod(z.object({ agenda_columns: z.array(AgendaColumnSchema) }), result)
      .agenda_columns
  },

  updateNumbering: async (id: number, payload: UpdateNumberingPayload, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/update_numbering`,
      payload,
      dealId,
    )
    return parseWithZod(
      AgendaSchema.pick({ number_styles: true, number_resets: true, always_nest_numbers: true }),
      result,
    )
  },

  bulkAddAgendaObjects: async (
    id: number,
    payload: BulkAddAgendaObjectsPayload,
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/bulk_add_agenda_objects`,
      payload,
      dealId,
    )

    return parseWithZod(AgendaContentSchema, result)
  },

  getAgendaImports: async (agendaId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(
      `${regionHost}/api/agendas/${agendaId}/agenda_imports`,
      undefined,
      dealId,
    )
    return parseWithZod(AgendaImportsResponseSchema, result).agenda_imports
  },

  createAgendaImport: async (dealId: number, uploadId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agenda_imports`,
      { agenda_import: { upload_id: uploadId } },
      dealId,
    )
    return parseWithZod(AgendaImportResponseSchema, result).agenda_import
  },

  getAgendaImport: async (importId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/agenda_imports/${importId}`, undefined, dealId)
    return parseWithZod(AgendaImportResponseSchema, result).agenda_import
  },

  deleteAgendaImport: async (importId: number, dealId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.delete(`${regionHost}/api/agenda_imports/${importId}`, dealId)
  },

  createItemsFromDmsRef: async (
    id: number,
    payload: CreateItemsFromDmsRefPayload,
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/agendas/${id}/create_items_from_dms_ref`,
      payload,
      dealId,
    )

    return parseWithZod(CreateItemsFromDmsRefResponseSchema, result)
  },

  completeAgendaImport: async (
    importId: number,
    payload: CompleteAgendaImportPayload,
    dealId: number,
  ) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/agenda_imports/${importId}/complete_import`, payload, dealId)
  },
}
